import React, { useEffect } from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { Ionicons } from '@expo/vector-icons';
import { useSelector, useDispatch } from 'react-redux';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { forceLogout } from '../store/slices/authSlice';
import Logo from '../components/common/Logo';
import AuthGuard from '../components/common/AuthGuard';

// Auth Screens
import LoginScreen from '../screens/auth/LoginScreen';
import SplashScreen from '../screens/auth/SplashScreen';

// App Screens
import DashboardScreen from '../screens/DashboardScreen';
import MissionListScreen from '../screens/MissionListScreen';
import MissionDetailScreen from '../screens/MissionDetailScreen';
import ReportListScreen from '../screens/ReportListScreen';
import ReportDetailScreen from '../screens/ReportDetailScreen';
import ProfileScreen from '../screens/ProfileScreen';
import PilotProfileScreen from '../screens/PilotProfileScreen';
import PilotDashboardScreen from '../screens/PilotDashboardScreen';
import PilotMissionsScreen from '../screens/PilotMissionsScreen';
import PilotMissionDetailScreen from '../screens/PilotMissionDetailScreen';
import PilotApplicationRecordScreen from '../screens/PilotApplicationRecordScreen';
import PilotMultispectralCompleteScreen from '../screens/PilotMultispectralCompleteScreen';
import OperatorMissionsScreen from '../screens/OperatorMissionsScreen';
import OperatorFleetScreen from '../screens/OperatorFleetScreen';
import PilotAircraftScreen from '../screens/PilotAircraftScreen';

const Stack = createNativeStackNavigator();
const Tab = createBottomTabNavigator();

// Estilo común para todos los encabezados
const commonHeaderStyle = {
  headerStyle: {
    backgroundColor: '#2c3e50',
  },
  headerTintColor: '#fff',
  headerTitleStyle: {
    fontWeight: 'bold',
  }
};

// Logo personalizado para el header
const LogoTitle = () => (
  <View style={styles.headerLogo}>
    <Logo width={170} height={45} />
  </View>
);

// Navegador para misiones - Separado en dos stacks independientes
const MissionsListStack = () => (
  <Stack.Navigator screenOptions={commonHeaderStyle}>
    <Stack.Screen 
      name="MissionList" 
      component={MissionListScreen} 
      options={{ 
        title: 'Misiones',
        headerRight: () => <LogoTitle />
      }} 
    />
  </Stack.Navigator>
);

const MissionDetailStack = () => (
  <Stack.Navigator screenOptions={commonHeaderStyle}>
    <Stack.Screen 
      name="MissionDetail" 
      component={MissionDetailScreen} 
      options={{ 
        title: 'Detalle de Misión',
        headerRight: () => <LogoTitle />
      }} 
    />
  </Stack.Navigator>
);

const OperatorStack = () => (
  <Stack.Navigator screenOptions={commonHeaderStyle}>
    <Stack.Screen
      name="OperatorMissionsList"
      component={OperatorMissionsScreen}
      options={{ title: 'Operaciones', headerRight: () => <LogoTitle /> }}
    />
    <Stack.Screen
      name="OperatorFleet"
      component={OperatorFleetScreen}
      options={{ title: 'Mi Flota', headerRight: () => <LogoTitle /> }}
    />
    <Stack.Screen
      name="PilotMissionDetail"
      component={PilotMissionDetailScreen}
      options={{ title: 'Detalle de Misión', headerRight: () => <LogoTitle /> }}
    />
  </Stack.Navigator>
);

const PilotStack = () => (
  <Stack.Navigator screenOptions={commonHeaderStyle}>
    <Stack.Screen
      name="PilotDashboardHome"
      component={PilotDashboardScreen}
      options={{ title: 'Inicio', headerRight: () => <LogoTitle /> }}
    />
    <Stack.Screen
      name="PilotMissionDetail"
      component={PilotMissionDetailScreen}
      options={{ title: 'Detalle de Misión', headerRight: () => <LogoTitle /> }}
    />
    <Stack.Screen
      name="PilotApplicationRecord"
      component={PilotApplicationRecordScreen}
      options={{ title: 'Registrar aplicación', headerRight: () => <LogoTitle /> }}
    />
    <Stack.Screen
      name="PilotMultispectralComplete"
      component={PilotMultispectralCompleteScreen}
      options={{ title: 'Entregar archivos TIFF', headerRight: () => <LogoTitle /> }}
    />
  </Stack.Navigator>
);

const PilotMissionsStack = () => (
  <Stack.Navigator screenOptions={commonHeaderStyle}>
    <Stack.Screen
      name="PilotMissionsList"
      component={PilotMissionsScreen}
      options={{ title: 'Misiones', headerRight: () => <LogoTitle /> }}
    />
    <Stack.Screen
      name="PilotMissionDetail"
      component={PilotMissionDetailScreen}
      options={{ title: 'Detalle de Misión', headerRight: () => <LogoTitle /> }}
    />
    <Stack.Screen
      name="PilotApplicationRecord"
      component={PilotApplicationRecordScreen}
      options={{ title: 'Registrar aplicación', headerRight: () => <LogoTitle /> }}
    />
    <Stack.Screen
      name="PilotMultispectralComplete"
      component={PilotMultispectralCompleteScreen}
      options={{ title: 'Entregar archivos TIFF', headerRight: () => <LogoTitle /> }}
    />
  </Stack.Navigator>
);

const MainNavigator = () => {
  const { user } = useSelector(state => state.auth);
  const isEnterpriseUser  = user?.is_enterprise_user && !user?.is_pilot && !user?.is_staff;
  const isPilot           = user?.is_pilot;
  const isOperatorAdmin   = user?.is_drone_operator;
  const showPilotStack    = isPilot || isOperatorAdmin;


  return (
    <AuthGuard>
      <Tab.Navigator
      screenOptions={({ route }) => ({
        tabBarIcon: ({ focused, color, size }) => {
          let iconName;
          if (route.name === 'Dashboard') {
            iconName = focused ? 'home' : 'home-outline';
          } else if (route.name === 'PilotMissions') {
            iconName = focused ? 'list' : 'list-outline';
          } else if (route.name === 'OperatorMissions') {
            iconName = focused ? 'business' : 'business-outline';
          } else if (route.name === 'OperatorFleetTab') {
            iconName = focused ? 'airplane' : 'airplane-outline';
          } else if (route.name === 'PilotAircraftTab') {
            iconName = focused ? 'airplane' : 'airplane-outline';
          } else if (route.name === 'Missions' || route.name === 'Reports') {
            iconName = focused ? 'list' : 'list-outline';
          } else if (route.name === 'Profile') {
            iconName = focused ? 'person' : 'person-outline';
          }
          return <Ionicons name={iconName} size={size} color={color} />;
        },
        tabBarActiveTintColor: '#2c3e50',
        tabBarInactiveTintColor: 'gray',
        ...commonHeaderStyle,
      })}
    >
      <Tab.Screen
        name="Dashboard"
        component={showPilotStack ? PilotStack : DashboardScreen}
        options={{
          title: 'Inicio',
          headerShown: false,
          headerRight: () => <LogoTitle />
        }}
      />

      {/* Tab Mis misiones — solo si es piloto */}
      {isPilot && (
        <Tab.Screen
          name="PilotMissions"
          component={PilotMissionsStack}
          options={{ headerShown: false, title: 'Mis misiones' }}
        />
      )}

      {/* Tab Operaciones — solo si es admin de operador */}
      {isOperatorAdmin && (
        <Tab.Screen
          name="OperatorMissions"
          component={OperatorStack}
          options={{ headerShown: false, title: 'Operaciones' }}
        />
      )}

      {/* Tab Mi Flota — solo si es admin de operador */}
      {isOperatorAdmin && (
        <Tab.Screen
          name="OperatorFleetTab"
          component={OperatorFleetScreen}
          options={{ title: 'Mi Flota', headerRight: () => <LogoTitle /> }}
        />
      )}

      {/* Tab Mis Aeronaves — solo si es piloto independiente (no admin de operador) */}
      {isPilot && !isOperatorAdmin && (
        <Tab.Screen
          name="PilotAircraftTab"
          component={PilotAircraftScreen}
          options={{ title: 'Mis Aeronaves', headerRight: () => <LogoTitle /> }}
        />
      )}

      {/* Tabs para usuarios no piloto */}
      {!showPilotStack && isEnterpriseUser && (
        <Tab.Screen
          name="Reports"
          component={ReportListScreen}
          options={{ title: 'Reportes', headerRight: () => <LogoTitle /> }}
        />
      )}
      {!showPilotStack && !isEnterpriseUser && (
        <Tab.Screen
          name="Missions"
          component={MissionsListStack}
          options={{ headerShown: false, title: 'Misiones' }}
        />
      )}
      
      <Tab.Screen 
        name="Profile" 
        component={ProfileScreen} 
        options={{ 
          title: 'Perfil',
          headerRight: () => <LogoTitle />
        }} 
      />
      
      <Tab.Screen 
        name="MissionDetailTab" 
        component={MissionDetailStack}
        options={{ 
          headerShown: false,
          tabBarButton: () => null,
          tabBarVisible: false,
        }} 
      />


      <Tab.Screen 
        name="ReportDetail" 
        component={ReportDetailScreen}
        options={{ 
          headerShown: true,
          title: 'Detalle de Reporte',
          headerRight: () => <LogoTitle />,
          tabBarButton: () => null, // Ocultar esta pestaña
          tabBarVisible: false,
        }} 
      />
      
      <Tab.Screen 
        name="PilotProfile" 
        component={PilotProfileScreen}
        options={{ 
          headerShown: true,
          title: 'Perfil del Piloto',
          headerRight: () => <LogoTitle />,
          tabBarButton: () => null, // Ocultar esta pestaña
          tabBarVisible: false,
        }} 
      />
    </Tab.Navigator>
    </AuthGuard>
  );
};

// Navegador de autenticación (cuando el usuario no está autenticado)
const AuthNavigator = () => {
  return (
    <Stack.Navigator screenOptions={{ headerShown: false }}>
      <Stack.Screen name="Login" component={LoginScreen} options={{ title: 'AMissionHub — Inicia sesión' }} />
    </Stack.Navigator>
  );
};

// Navegador principal que decide qué mostrar según el estado de autenticación
const AppNavigator = () => {
  const { isAuthenticated, isLoading } = useSelector(state => state.auth);
  const dispatch = useDispatch();
  
  // Escuchar evento de token expirado y verificar estado inicial
  useEffect(() => {
    const handleTokenExpired = () => {
      console.log('Evento de token expirado recibido, forzando logout');
      dispatch(forceLogout());
    };
    
    // Verificar estado inicial cuando la app se monta
    const checkInitialAuth = async () => {
      if (!isLoading && isAuthenticated) {
        const { getToken } = await import('../services/auth/authService');
        const token = await getToken();
        if (!token) {
          console.log('Token no encontrado al montar app, forzando logout');
          dispatch(forceLogout());
        }
      }
    };
    
    checkInitialAuth();
    
    if (typeof window !== 'undefined') {
      window.addEventListener('auth:token-expired', handleTokenExpired);
      
      return () => {
        window.removeEventListener('auth:token-expired', handleTokenExpired);
      };
    }
  }, [dispatch, isLoading, isAuthenticated]);
  
  // Mostrar pantalla de carga mientras se verifica la autenticación
  if (isLoading) {
    return <SplashScreen />;
  }
  
  return (
    <Stack.Navigator screenOptions={{ headerShown: false }}>
      {isAuthenticated ? (
        <Stack.Screen name="Main" component={MainNavigator} />
      ) : (
        <Stack.Screen name="Auth" component={AuthNavigator} />
      )}
    </Stack.Navigator>
  );
};

const styles = StyleSheet.create({
  headerLogo: {
    marginRight: 10,
  }
});

export default AppNavigator;